<template>
  <div class="container-user">
    <div class="row flex-column flex-lg-row" :class="{ 'user-layout-row': branchId, 'left-collapsed': isLeftCollapsed && resultShown }">

      <!-- 1. Assessment breadcrumbs — always top on mobile, left-top on desktop -->
      <div
        class="pt-2 transition-width order-1 order-lg-1 assessment-col"
        :class="[
          branchId ? 'col-12 col-lg-5 container-left with-border' : 'col-12 tnm-bg',
          { 'collapsed': isLeftCollapsed && resultShown }
        ]"
      >
        <app-tnm-stage-selector
          :branchId.sync="branchId"
          :hideSafetySelector.sync="hideSafetySelector"
          :hideTreatmentResultCheckbox.sync="hideTreatmentResultCheckbox"
          :completeData.sync="completeData"
          :excludedRecommendations.sync="excludedRecommendations"
        />
      </div>

      <!-- 2. Progress bar + Current question — middle on mobile, right on desktop -->
      <div
        v-if="branchId"
        class="col-12 col-lg-7 container-right pt-3 my-large-container transition-width order-2 order-lg-2 question-col"
        :class="[
          { 'with-bg': !resultShown },
          { 'expanded': isLeftCollapsed && resultShown }
        ]"
      >
        <!-- Collapse/Expand Toggle (desktop only) -->
        <button
          v-if="resultShown"
          @click="toggleLeftPanel"
          class="collapse-toggle-btn"
          :title="isLeftCollapsed ? 'Show Questions' : 'Hide Questions'"
        >
          <span v-if="isLeftCollapsed">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="15 18 9 12 15 6"></polyline>
            </svg>
          </span>
          <span v-else>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
          </span>
        </button>

        <app-question-progress
          v-if="branchId"
          :branchId.sync="branchId"
          :questionNo="questionNo"
          :questionCount="questionCount"
          :hideNavigation="resultShown && completeData"
          :completeData="completeData"
          :onToogleResult="toogleResult"
          :groups.sync="groups"
          :answers.sync="answers"
          :currentQuestionId="localCurrentQuestionid"
          @currentQuestion="setCurrentQuestion"
          @currentQuestionIndex="setCurrentQuestionIndex"
        />

        <div v-if="branchId">
          <app-question
            v-if="currentQuestion && !resultShown"
            :key="currentQuestion.id"
            :property="currentQuestion"
            :answers.sync="answers"
            :treatmentHistory.sync="treatmentHistory"
            :excludedRecommendations.sync="excludedRecommendations"
            :branchId.sync="branchId"
            :localGroups.sync="localGroups"
            :localQuestions.sync="localQuestions"
          />
          <app-question-result
            v-show="completeData && resultShown"
            :branchId.sync="branchId"
            :groups.sync="groups"
            :answers.sync="answers"
            :treatmentHistory.sync="treatmentHistory"
            :hideSafetySelector.sync="hideSafetySelector"
            :completeData.sync="completeData"
            :excludedRecommendations.sync="excludedRecommendations"
          />
        </div>
      </div>

      <!-- 3. Question groups (Patient status, Diagnostic Status…) — bottom on mobile, left-bottom on desktop -->
      <div
        class="transition-width order-3 order-lg-3 question-list-col"
        :class="[
          branchId ? 'col-12 col-lg-5 container-left with-border' : 'col-12',
          { 'collapsed': isLeftCollapsed && resultShown }
        ]"
      >
        <app-question-list
          :branchId.sync="branchId"
          :questionNo.sync="questionNo"
          :questionCount.sync="questionCount"
          :groups.sync="groups"
          :localGroups.sync="localGroups"
          :localQuestions.sync="localQuestions"
          :answers.sync="answers"
          :treatmentHistory.sync="treatmentHistory"
          :currentQuestionId="localCurrentQuestionid"
          @currentQuestion="setCurrentQuestion"
          @currentQuestionIndex="setCurrentQuestionIndex"
        />
      </div>

    </div>
  </div>
</template>

<script lang="ts">
import { Component, Vue } from "vue-property-decorator";
import AppDiseaseTreeSelector from "@/components/AppDiseaseTreeSelector.vue";
import AppQuestionnaire from "@/components/questionnaire/AppQuestionnaire.vue";
import AppDraggableAndResizableWrapper from "@/components/AppDraggableAndResizableWrapper.vue";
import AppQuestionnaireResult from "@/components/questionnaire/AppQuestionnaireResult.vue";
import { Group } from "@/entities/questionaire/Group";
import { Answer } from "@/entities/questionaire/answers/Answer";
import { Treatment } from "@/entities/Treatment";
import AppTnmStageSelector from "@/components/AppTnmStageSelector.vue";
import AppQuestionList from "@/components/questionnaire/AppQuestionList.vue";
import AppQuestionProgress from "@/components/questionnaire/AppQuestionProgress.vue";
import AppQuestion from "@/components/questionnaire/AppQuestion.vue";
import AppQuestionResult from "@/components/questionnaire/AppQuestionResult.vue";
import { Property } from "@/entities/questionaire/Property";

@Component({
  components: {
    AppDiseaseTreeSelector,
    AppQuestionnaire,
    AppDraggableAndResizableWrapper,
    AppQuestionnaireResult,
    AppTnmStageSelector,
    AppQuestionList,
    AppQuestionProgress,
    AppQuestion,
    AppQuestionResult,
  },
})
export default class UserHome extends Vue {
  public branchId: number | null = null;
  public groups: Group[] = [];
  public answers: Answer[] = [];
  public treatmentHistory: Treatment[] = [];
  public completeData: boolean = false;
  public hideSafetySelector: boolean = false;
  public hideTreatmentResultCheckbox: boolean = false;
  public currentQuestion: any | null = null;
  public questionNo: number = 0;
  public questionCount: number = 0;
  public resultShown: boolean = false;
  public localCurrentQuestionid: number = 0;
  public excludedRecommendations: {
    id: number;
    name: string;
    approvalPercentage: number;
  }[] = [];
  public localGroups: Group[] = [];
  public localQuestions: Property[] = [];
  public isLeftCollapsed: boolean = false;

  setCurrentQuestion(data: any) {
    this.currentQuestion = data;
    this.resultShown = data == null;
    
    // Reset collapse state when returning to questions
    if (!this.resultShown) {
      this.isLeftCollapsed = false;
    }
    
    this.localCurrentQuestionid = this.currentQuestion
      ? this.currentQuestion.id
      : -1;
  }

  setCurrentQuestionIndex(index: number) {
    this.questionNo = index;
  }

  toogleResult() {
    if (!this.currentQuestion) return;

    this.resultShown = !this.resultShown;
    
    // Reset collapse state when toggling back to questions
    if (!this.resultShown) {
      this.isLeftCollapsed = false;
    }
  }

  toggleLeftPanel() {
    this.isLeftCollapsed = !this.isLeftCollapsed;
  }
}
</script>

<style lang="scss" scoped>
@import url("https://fonts.googleapis.com/css2?family=Manrope:wght@500&display=swap");

.container-user {
  font-family: "Manrope", sans-serif;
  width: 100%;
  max-width: 1960px;
  margin: 0 auto;
  padding: 0;
}

.transition-width {
  transition: all 0.3s ease-in-out;
}

.container-left {
  background-color: #ffffff;
  overflow: hidden;

  &.with-border {
    border-right: none;
    background-color: #f8fafc;
  }

  &.collapsed {
    padding: 0 !important;
    border: none !important;
    opacity: 0;
    overflow: hidden;
  }
}

.tnm-bg {
  background-position-y: bottom;
  background-position-x: right;
  background-repeat: no-repeat;
  background-size: contain;
  min-height: 300px;
}

.container-right {
  background-color: #ffffff;
  min-height: 400px;
  position: relative;
}

.collapse-toggle-btn {
  position: absolute;
  left: 10px;
  top: 20px;
  z-index: 1000;
  background: #ffffff;
  border: 1px solid #dae4ee;
  border-radius: 6px;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  &:hover {
    background: #f8fafc;
    border-color: #94a3b8;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.15);
  }

  &:active {
    transform: scale(0.95);
  }

  svg {
    color: #475569;
  }
}


@media (max-width: 600px) {
  .collapse-toggle-btn {
    display: none;
  }
}
.my-large-container {
  width: 100%;
  margin: 0;
  //padding: 0 15px;
}

.row {
  margin-left: 0;
  margin-right: 0;
}

/* Tablet Breakpoint */
@media (min-width: 768px) {
  .tnm-bg {
    min-height: 400px;
  }
  
  .container-right {
    min-height: 500px;
  }
}

/* Desktop Breakpoint */
@media (min-width: 992px) {
  .container-left {
    &.with-border {
      border-right: 1px solid #dae4ee;
    }
  }

  .tnm-bg {
    min-height: 476px;
  }

  .container-right {
    min-height: 600px;
  }

  /* CSS Grid layout: assessment + question-list share left column, question spans full right height */
  .user-layout-row {
    display: grid !important;
    grid-template-columns: 41.666% 58.334%;
    grid-template-rows: auto 1fr;
    margin-left: 0 !important;
    margin-right: 0 !important;
  }

  .assessment-col {
    grid-column: 1;
    grid-row: 1;
    max-width: 100%;
    flex: none;
    padding: 0;
  }

  .question-col {
    grid-column: 2;
    grid-row: 1 / span 2;
    max-width: 100%;
    flex: none;
    padding: 0;
  }

  .question-list-col {
    grid-column: 1;
    grid-row: 2;
    max-width: 100%;
    flex: none;
    padding: 0;
  }

  /* Collapsed left panel: shrink column 1 to 0, question spans full width */
  .user-layout-row.left-collapsed {
    grid-template-columns: 0 100%;
  }

  .user-layout-row.left-collapsed .question-col {
    grid-column: 1 / span 2;
  }
}

/* Large Desktop Breakpoint */
@media (min-width: 1200px) {
  .my-large-container {
    max-width: 1140px;
   // padding: 0 15px;
  }
}

/* Extra Large Desktop */
@media (min-width: 1400px) {
  .container-user {
    max-width: 1960px;
  }
  
  .my-large-container {
    max-width: 1300px;
  }
}

/* Ultra Wide Screens */
@media (min-width: 1920px) {
  .container-user {
    max-width: 1960px;
  }
  
  .my-large-container {
    max-width: 1940px;
  }
}

/* Mobile Specific Styles */
@media (max-width: 767px) {
  .container-user {
    padding: 0;
  }

  .container-left,
  .container-right {
    padding-left: 10px;
    padding-right: 10px;
  }

  .my-large-container {
    padding: 0 10px;
  }

  .tnm-bg {
    min-height: 250px;
    background-size: 80%;
  }

  /* Assessment section — compact on mobile */
  .container-left {
    padding-top: 0.5rem !important;
    padding-bottom: 0.5rem !important;
  }

  /* Question section — tight top padding */
  .container-right {
    min-height: 300px;
    margin-top: 0;
    margin-bottom: 0;
    padding-top: 0.75rem !important;

    &.with-bg {
      background-size: 70%;
    }
  }

  /* Question list section — visual separator from question above */
  .question-list-col {
    border-top: 1px solid #e2e8f0;
    padding-top: 0.5rem !important;
    padding-bottom: 1rem !important;
  }

  .collapse-toggle-btn {
    width: 32px;
    height: 32px;
    left: 8px;
    top: 16px;

    svg {
      width: 18px;
      height: 18px;
    }
  }
}

/* Small Mobile */
@media (max-width: 480px) {
  .container-user {
    min-width: 100%;
  }

  .container-left,
  .container-right,
  .question-list-col {
    padding-left: 8px;
    padding-right: 8px;
  }

  .my-large-container {
    padding: 0 8px;
  }

  .tnm-bg {
    min-height: 200px;
    background-size: 70%;
  }

  .container-right {
    min-height: 250px;

    &.with-bg {
      background-size: 60%;
    }
  }
}
</style>